import queryString from 'core/libs/query-string';
import { prefillZero } from 'core/utils/string-helper';
import { isDateValid } from 'core/utils/dates';

/**
 * Возвращает правильную ссылку для постраничной навигации
 * учитывая гет параметры и хеш.
 */
export function pageLinkConstructor(page, location) {
  const { pathname, search, hash } = location;
  const query = queryString.stringify({
    ...queryString.parse(search),
    page: page === 1 ? undefined : page,
  });

  return `${pathname}${query ? '?' : ''}${query}${hash}`;
}

/**
 * Возвращает полную ссылку с протоколом.
 * Добавляет протокол к ссылке, если он не указан.
 * Добавляет hostName к ссылке.
 *
 * TODO: При необходимости нужно доработать утилиту, чтобы правильно
 * обрабатывать эти случаи:
 * getFullLink(('link', 'https://wmj.ru'));
 * getFullLink(('/link', 'https://wmj.ru/'));
 * getFullLink(('/link', 'wmj.ru'));
 *
 * @param {string} link
 * @param {string} hostName
 */
export function getFullLink(link, hostName) {
  if (!link) {
    return hostName;
  }
  if (!link.indexOf('//')) {
    return `https://${link.replace(/^\/\//, '')}`;
  }
  if (!/^https?:\/\//.test(link) && hostName) {
    return hostName + link;
  }
  return link;
}

export function createPath(location) {
  return location.pathname + location.search;
}

/**
 * Проверка номера страницы.
 *
 * page == 1 считается невалидным, т.к это дубль корневой страницы.
 * Отсутствие page (undefined) считается валидным, т.к это корневая страница
 */
export function checkPageUrlParam(location, total) {
  const { page } = queryString.parse(location.search);
  return page === undefined || page <= total && page > 1 && !(page % 1);
}

export function getPage(search) {
  return Number(queryString.parse(search).page) || 1;
}

/**
 *
 * @param {string} search значение `location.search`
 * @param {number} limit кол-во элементов на каждой странице
 * @returns {number}
 */
export function getOffset(search, limit) {
  const limitValue = +limit;
  const limitType = typeof limitValue;

  if (limitType !== 'number' || isNaN(limitValue)) {
    throw new Error('[getOffset] Cannot convert `limit` to a number (got `' + limit + '` of type ' + typeof limit + ')');
  }

  return (getPage(search) - 1) * limitValue;
}

/**
 * Формирует ссылку на рубрику
 *
 * @param {string} slug
 * @param {string} rootSlug
 * @param {string} prefix - для проектов с раздельной сервисной/медийной частями
 * необходимо явно указывать путь до медиа части проекта,
 * где ответсвенность за добавление начального слеш остается за юзером, а не утилитой.
 * @returns {string}
 */
export function buildRubricUrl(slug, rootSlug, prefix) {
  const path = prefix || '';

  if ((slug === rootSlug) || !rootSlug) {
    return `${path}/${slug}`;
  }
  return `${path}/${rootSlug}/${slug}`;
}

/**
 * Возвращает относительный адрес страницы в виде строки.
 *
 * @param {object} location
 */
export function createUriFromLocation({ pathname = '/', search = '', hash = '' } = {}) {
  return pathname + search + hash;
}


/**
 * Формирует имя файла для скачивания из хоста и ссылки на изображение.
 *
 * @param {string} host
 * @param {string} src
 * @returns {string}
 */
export function buildDownloadFileName(host, src) {
  if (typeof host !== 'string' || typeof src !== 'string') return 'image';

  return host.replace(/(?:[^\s]+\.)?(\w+)\.\w+$/, '$1')
    + '-'
    + src.substring(src.lastIndexOf('/') + 1);
}

export function removeDuplicateSlashes(url) {
  if (typeof url !== 'string') return '';
  return url.replace(/^\/+/, '');
}

/**
 * @param prefix добавляет префикс к /search, если сайт состоит из медиа и сервиса
 * @param query строка поискового запроса
 * @param key используется для дополнительных фильтров по "рубрике"
 * @param value используется для дополнительных фильтров по "рубрике"
 * @returns {string}
 */
export function buildSearchLink(prefix, query, key, value) {
  const newParams = { query };
  if (key && value) {
    newParams[key] = value;
  }
  return `${prefix || ''}/search?${queryString.stringify(newParams)}`;
}

/**
 * Проверяет хост на наличие поддомена free.
 * Поддомен free используется в социально значимых сайтах РКН.
 */
export function checkFreeHost(host) {
  return /^free\./.test(host);
}

export function getDomainIdentifier(host) {
  return host
    .replace(/^(www.)/, '')
    .replace(/(.\w+)$/, '');
}

/**
 * Получает из хоста домен в виде '.example.ru'
 */
export function getPublisherScope(host) {
  return host && `.${host.replace(/^(www.)/, '')}`;
}

/**
 * Проверяет pathname на принадлежность к медийной части.
 */
export function isMediaPath(pathname, mediaUrlPrefix) {
  return mediaUrlPrefix
    ? pathname.indexOf(mediaUrlPrefix) === 0 || pathname.indexOf('/preview' + mediaUrlPrefix) === 0
    : true;
}

export function removePreviewPath(path) {
  return path.replace(/^\/preview/i, '');
}

/**
 * Функция, получающая все совпадения ссылок по текущему URL страницы
 */
export function getCurrentMenuMatches(items, location, isAmp) {
  const url = createPath(location);
  return items
    .filter(item => url.indexOf(`${isAmp ? '/amp' : ''}${item.link}`) === 0)
    .sort((a, b) => b.link.length - a.link.length);
}

/**
 * Функция, преобразующая ссылку для получения пуидов
 */
export function getPuidsLink(link) {
  if (typeof link !== 'string') return null;

  return link
    // убираем ведущий слеш и префикс /amp/
    .replace(/^\/(amp\/)?/, '')
    // префикс /comments
    .replace(/(\/comments)?$/, '');
}


/**
 * Парсит параметр `page[day]` из атрибута `links`, возвращаемого
 * ручкой `/topics`
 *
 * @param {string} apiLink относительный урл
 * @returns {?string} значение параметра `page[day]` из apiLink, или null
 */
export function getPageDayFromApiLink(apiLink = '') {
  const url = new URL(apiLink, 'https://whatever.ru');
  return url.searchParams.get('page[day]') || null;
}


/**
 * Строит объект Date из `location.match`
 *
 * @param {object} matchParams значение match.params реакт-роутера
 * @param {string|number} matchParams.year год
 * @param {string|number} [matchParams.month=1] месяц
 * @param {string|number} matchParams.day день
 * @returns {?object} объект Date, в случае невалидной даты - `null`
 */
export function getDateFromMatch(matchParams = {}) {
  const { year, month = 1, day } = matchParams;
  // последним аргументом сводим к минимуму возможность неверной
  // интерпретации дня из-за различных часовых поясов
  const date = new Date(year, month - 1, day, 12);

  return isDateValid(date) ? date : null;
}


/**
 * Дополняет базовый урл значением из date
 *
 * @param {string} baseUrl базовый урл без '/' в конце, к которому надо добавить дату
 * @param {object|string} date объект Date или строка, которая будет передана
 * @param {boolean} withUTC модификатор использования UTC
 *    в его конструктор
 * @returns {string} адрес страницы для указанной даты
 */
export function getUrlWithDate(baseUrl = '', date, withUTC = true) {
  if (!date) return baseUrl;

  const dateObject = new Date(date);

  if (!isDateValid(dateObject)) return baseUrl;

  const utcSuffix = withUTC ? 'UTC' : '';

  return baseUrl + '/'
    + dateObject[`get${utcSuffix}FullYear`]() + '/'
    + prefillZero(dateObject[`get${utcSuffix}Month`]() + 1) + '/'
    + prefillZero(dateObject[`get${utcSuffix}Date`]());
}


/**
 * Формирует значение параметра `page[day]` для ручки `/topics`
 *
 * @param {object} matchParams значение match.params реакт-роутера
 * @param {boolean} withUTC модификатор использования UTC
 * @returns {string} значение для передачи в параметр апи `page[day]`
 */
export function getPageDayFromMatch(matchParams = {}) {
  const date = getDateFromMatch(matchParams);
  return date
    ? date.getUTCFullYear() + '-'
      + prefillZero(date.getUTCMonth() + 1) + '-'
      + prefillZero(date.getUTCDate())
    : 'first';
}


/**
 * Проверяет является ли страница превью или нет
 *
 * @param {object} location
 * @returns {boolean}
 */
export function hasPreviewToken(search = {}) {
  return typeof queryString.parse(search).preview_token !== 'undefined';
}


// обработка кирилличиских ссылок
export function decodeURL(url) {
  if (!isURLEncoded(url)) return url;

  let decoded = url;
  try {
    decoded = decodeURI(url);
  } catch (e) {
    console.error(e);
  }
  return decoded;
}

function isURLEncoded(url) {
  return /%(?:[0-9A-Fa-f]{2})+/.test(url);
}
